import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Eye, EyeOff, RefreshCw, Filter } from 'lucide-react';
import { SettingRow } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConfigFormShell, jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';
import styles from './AccessControlTab.module.css';

const SIGNED_URL_DOCS = 'https://docs.vnetwork.vn/docs/cdn/multi-cdn/signed-url-tokens';

type ListKind = 'ip' | 'asn' | 'geo';
interface ListRule { _id: string; values: string[]; action: string; priority: number }
interface TokenRule { _id: string; rule_name: string; algorithm: string; secret_key: string; match_path_prefixes: string[]; path_segments: number; enabled: boolean; priority: number }
interface Form {
  enabled: boolean;
  default_action: string;
  ip_rules: ListRule[];
  asn_rules: ListRule[];
  geo_rules: ListRule[];
  token_enabled: boolean;
  token_whitelist_ips: string[];
  token_rules: TokenRule[];
}
let _ac = 0;
const lid = () => `ac${++_ac}`;
// BE expects lowercase `allow` / `deny` (CDNAccessControlConfig.default_action +
// each rule's action). Sending uppercase silently broke matching.
const ACTIONS: { value: string; label: string }[] = [
  { value: 'allow', label: 'Allow' },
  { value: 'deny', label: 'Deny' },
];
const normAction = (a: unknown, fallback: string) => {
  const s = String(a ?? '').toLowerCase();
  return s === 'allow' || s === 'deny' ? s : fallback;
};
const valuesKey: Record<ListKind, string> = { ip: 'cidrs', asn: 'asns', geo: 'countries' };

const csv = (a: unknown) => (Array.isArray(a) ? (a as unknown[]).map(String).join(', ') : '');
const split = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

// Mirrors the legacy console: SHA256 → 32 chars, MD5 → 16. Crypto-random.
function generateRandomKey(algorithm = 'HMAC-SHA256'): string {
  const length = algorithm === 'HMAC-MD5' ? 16 : 32;
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint8Array(length);
  window.crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < arr.length; i += 1) out += chars[arr[i] % chars.length];
  return out;
}

// Secret key with show/hide eye toggle + regenerate, matching the old TokenRuleDialog.
function SecretField({
  value,
  onChange,
  onRegenerate,
}: {
  value: string;
  onChange: (v: string) => void;
  onRegenerate: () => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
      <Input
        style={{ flex: 1 }}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Secret key…"
      />
      <IconButton variant="ghost" size="sm" aria-label={show ? 'Hide secret' : 'Show secret'} onClick={() => setShow((s) => !s)}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </IconButton>
      <IconButton variant="ghost" size="sm" aria-label="Generate random key" title="Generate random secure key" onClick={onRegenerate}>
        <RefreshCw size={15} />
      </IconButton>
    </div>
  );
}

function hydrateList(arr: unknown, kind: ListKind): ListRule[] {
  if (!Array.isArray(arr)) return [];
  return (arr as Record<string, unknown>[]).map((r) => ({
    _id: lid(),
    values: Array.isArray(r[valuesKey[kind]]) ? (r[valuesKey[kind]] as unknown[]).map(String) : [],
    action: normAction(r.action, 'deny'),
    priority: Number(r.priority) || 0,
  }));
}

function hydrate(c: Record<string, unknown>): Form {
  const tv = (c.token_v2 as Record<string, unknown>) || {};
  return {
    enabled: Boolean(c.enabled),
    default_action: normAction(c.default_action, 'allow'),
    ip_rules: hydrateList(c.ip_rules, 'ip'),
    asn_rules: hydrateList(c.asn_rules, 'asn'),
    geo_rules: hydrateList(c.geo_rules, 'geo'),
    token_enabled: Boolean(tv.enabled),
    token_whitelist_ips: Array.isArray(tv.whitelist_ips) ? (tv.whitelist_ips as string[]) : [],
    token_rules: Array.isArray(tv.rules)
      ? (tv.rules as Record<string, unknown>[]).map((r) => ({
        _id: lid(),
        // BE returns token rules as { name, secret, match_paths, algorithm:
        // hmac_sha256|md5 }. Tolerate the legacy FE field names too.
        rule_name: (r.name as string) || (r.rule_name as string) || '',
        algorithm: r.algorithm === 'md5' ? 'HMAC-MD5' : 'HMAC-SHA256',
        secret_key: (r.secret as string) || (r.secret_key as string) || '',
        match_path_prefixes: Array.isArray(r.match_paths)
          ? (r.match_paths as string[])
          : Array.isArray(r.match_path_prefixes)
            ? (r.match_path_prefixes as string[])
            : [],
        path_segments: Number(r.path_segments) || 0,
        enabled: r.enabled == null ? true : Boolean(r.enabled),
        priority: Number(r.priority) || 0,
      }))
      : [],
  };
}

function toWire(f: Form): Record<string, unknown> {
  const list = (rules: ListRule[], kind: ListKind) =>
    rules.map((r) => ({
      // ASNs must go to the BE as integers ([]int) — sending strings yields
      // "cannot unmarshal string ... of type int". IP/geo stay strings.
      [valuesKey[kind]]: kind === 'asn' ? r.values.map(Number).filter((n) => !Number.isNaN(n)) : r.values,
      action: r.action,
      priority: r.priority,
    }));
  return {
    enabled: f.enabled,
    default_action: f.default_action,
    ip_rules: list(f.ip_rules, 'ip'),
    asn_rules: list(f.asn_rules, 'asn'),
    geo_rules: list(f.geo_rules, 'geo'),
    token_v2: {
      enabled: f.token_enabled,
      whitelist_ips: f.token_whitelist_ips,
      // BE token-rule wire shape: { name, enabled, algorithm: hmac_sha256|md5,
      // secret, match_paths, path_segments, priority }.
      rules: f.token_rules.map((r) => ({
        name: r.rule_name,
        enabled: r.enabled,
        algorithm: r.algorithm === 'HMAC-MD5' ? 'md5' : 'hmac_sha256',
        secret: r.secret_key,
        match_paths: r.match_path_prefixes,
        path_segments: r.path_segments,
        priority: r.priority,
      })),
    },
  };
}

export function AccessControlTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg } = useCdnConfig(domainId, 'access-control', { vendorMarkers: ['enabled', 'default_action'] });
  const isConfigured = !!cfg;
  const [form, setForm] = useState<Form | null>(null);
  const [snap, setSnap] = useState('');
  useEffect(() => {
    const next = cfg ? hydrate(cfg) : hydrate({});
    setForm(next);
    setSnap(JSON.stringify(next));
  }, [cfg]);
  const dirty = useMemo(() => !!form && !jsonEqual(form, JSON.parse(snap || '{}')), [form, snap]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const update = useUpdateCdnConfig(domainId, 'access-control', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'access_control', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Access control saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save', description: errorMessage(e, 'Could not save') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'access-control', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'access_control', 'delete');
      show({ tone: 'success', title: 'Access control removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove', description: errorMessage(e, 'Could not remove') }),
  });
  const busy = update.isPending || remove.isPending;

  if (!form) {
    return (
      <ConfigFormShell title="Access Control" description={`IP, ASN, geo, and token rules for ${domain}.`} isLoading isConfigured={false} dirty={false} busy={false} onSave={() => { }}>
        <span />
      </ConfigFormShell>
    );
  }

  const listKey = (kind: ListKind) => (`${kind}_rules` as 'ip_rules' | 'asn_rules' | 'geo_rules');
  const addListRule = (kind: ListKind) => set(listKey(kind), [...form[listKey(kind)], { _id: lid(), values: [], action: 'deny', priority: 100 }]);
  const patchListRule = (kind: ListKind, id: string, c: Partial<ListRule>) =>
    set(listKey(kind), form[listKey(kind)].map((r) => (r._id === id ? { ...r, ...c } : r)));

  const ruleSection = (kind: ListKind, title: string, placeholder: string) => (
    <Card>
      <CardHeader
        actions={
          <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => addListRule(kind)}>
            Add
          </Button>
        }
      >
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {form[listKey(kind)].length === 0 ? (
          <EmptyState size="sm" icon={<Filter size={20} />} title={`No ${kind} rules yet`} description="Click Add to create one." />
        ) : (
          form[listKey(kind)].map((r) => (
            <div key={r._id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <Input style={{ flex: 2 }} placeholder={placeholder} value={csv(r.values)} onChange={(e) => patchListRule(kind, r._id, { values: split(e.target.value) })} />
              <Select value={r.action} onValueChange={(v) => patchListRule(kind, r._id, { action: v })}>
                <SelectTrigger style={{ width: 110 }}><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input style={{ width: 90 }} type="number" aria-label="Priority" value={String(r.priority)} onChange={(e) => patchListRule(kind, r._id, { priority: Number(e.target.value) || 0 })} />
              <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => set(listKey(kind), form[listKey(kind)].filter((x) => x._id !== r._id))}>
                <Trash2 size={15} />
              </IconButton>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );

  return (
    <ConfigFormShell
      title="Access Control"
      description={`IP, ASN, geo, and token rules for ${domain}.`}
      isConfigured={isConfigured}
      dirty={dirty}
      busy={busy}
      saving={update.isPending}
      statusLabel={form.enabled ? 'Enabled' : undefined}
      onSave={() => update.mutate(toWire(form))}
      onRemove={() => remove.mutate()}
      drift={<CdnAspectDrift domainId={domainId} path="access-control" />}
      onCancel={() => { if (snap) setForm(JSON.parse(snap)); }}
    >
      <Card>
        <CardHeader>
          <CardTitle>Global</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <SettingRow title="Enabled" control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />} />
          <Field label="Default action" description="Applied when no rule matches.">
            {(p) => (
              <Select value={form.default_action} onValueChange={(v) => set('default_action', v)}>
                <SelectTrigger {...p} style={{ maxWidth: 200 }}><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </Field>
        </CardBody>
      </Card>

      {ruleSection('ip', 'IP rules', '10.0.0.0/8, 1.2.3.4')}
      {ruleSection('asn', 'ASN rules', '13335, 15169')}
      {ruleSection('geo', 'Geo rules', 'US, VN, GB')}

      <Card>
        <CardHeader>
          <CardTitle>Signed URL tokens (v2)</CardTitle>
          <CardDescription>HMAC-signed URL validation for protected paths.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Alert tone="info">
            Tokens are HMAC-signed on your backend, then passed as leading URL path segments —
            the edge validates the signature and expiry before serving. See the{' '}
            <a className={styles.docLink} href={SIGNED_URL_DOCS} target="_blank" rel="noopener noreferrer">
              Signed URL tokens guide
            </a>{' '}
            for the signing algorithm (fixed vs dynamic) and code examples.
          </Alert>
          <SettingRow title="Enabled" control={<Switch checked={form.token_enabled} onCheckedChange={(v) => set('token_enabled', v)} />} />
          {form.token_enabled && (
            <>
              <Field label="Whitelist IPs" description="One per line — bypass token validation.">
                {(p) => <Textarea {...p} rows={2} value={form.token_whitelist_ips.join('\n')} onChange={(e) => set('token_whitelist_ips', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />}
              </Field>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)' }}>Token rules</span>
                <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => set('token_rules', [...form.token_rules, { _id: lid(), rule_name: '', algorithm: 'HMAC-SHA256', secret_key: generateRandomKey(), match_path_prefixes: [], path_segments: 0, enabled: true, priority: 100 }])}>
                  Add token rule
                </Button>
              </div>
              {form.token_rules.map((r) => (
                <div key={r._id} style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => set('token_rules', form.token_rules.filter((x) => x._id !== r._id))}>
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                  <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                    <Field label="Name">{(p) => <Input {...p} value={r.rule_name} onChange={(e) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, rule_name: e.target.value } : x)))} />}</Field>
                    <Field label="Algorithm" description="MD5 for standard CDN secure-link scripts.">
                      {(p) => (
                        <Select value={r.algorithm} onValueChange={(v) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, algorithm: v } : x)))}>
                          <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="HMAC-SHA256">HMAC-SHA256</SelectItem>
                            <SelectItem value="HMAC-MD5">HMAC-MD5 (deprecated)</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                    <Field label="Path segments">{(p) => <Input {...p} type="number" value={String(r.path_segments)} onChange={(e) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, path_segments: Number(e.target.value) || 0 } : x)))} />}</Field>
                    <Field label="Priority">{(p) => <Input {...p} type="number" value={String(r.priority)} onChange={(e) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, priority: Number(e.target.value) || 0 } : x)))} />}</Field>
                  </div>
                  <Field label="Secret key" description="HMAC signing key — toggle to reveal or regenerate.">
                    {() => (
                      <SecretField
                        value={r.secret_key}
                        onChange={(v) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, secret_key: v } : x)))}
                        onRegenerate={() => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, secret_key: generateRandomKey(x.algorithm) } : x)))}
                      />
                    )}
                  </Field>
                  <Field label="Match path prefixes" description="Comma-separated.">{(p) => <Input {...p} value={r.match_path_prefixes.join(', ')} onChange={(e) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, match_path_prefixes: split(e.target.value) } : x)))} />}</Field>
                  <SettingRow title="Enabled" control={<Switch checked={r.enabled} onCheckedChange={(v) => set('token_rules', form.token_rules.map((x) => (x._id === r._id ? { ...x, enabled: v } : x)))} />} />
                </div>
              ))}
            </>
          )}
        </CardBody>
      </Card>
    </ConfigFormShell>
  );
}
